import React, { useMemo, useState } from 'react';
import {
  Badge,
  EmptyState,
  ReportTable,
  type ReportTableColumn,
  type ReportTableSort,
} from '@roger/ui-components';
import type { Patient } from '../../store/slices/patientsSlice';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import OverflowTooltipText from '../OverflowTooltipText';
import { EpisodeNotesPill } from '../episode/EpisodeNotesPill';
import { patientCardSyncLine } from '../imported/syncReviewCopy';
import { ImportedPatientBadge } from './ImportedPatientBadge';
import { TestPatientBadge } from './TestPatientBadge';

const EMPTY = '—';

// `w-px` shrinks a column to its content; nowrap keeps that content whole.
const FIT_CONTENT = 'w-px whitespace-nowrap';
// `max-w-0` drops the cell's min-content to its header, so these columns
// truncate under a squeeze and are the only ones that take spare width.
const FLEXIBLE = 'max-w-0';

function episodeDatesLabel(patient: Patient): string {
  const start = formatDateToMMDDYYYY(patient.episode_start);
  const end = formatDateToMMDDYYYY(patient.episode_end);
  if (!start && !end) return EMPTY;
  return `${start || EMPTY} – ${end || EMPTY}`;
}

function buildColumns(showSyncReview: boolean): ReportTableColumn<Patient>[] {
  return [
    {
      id: 'last_name',
      header: 'Last name',
      accessor: 'last_name',
      enableSorting: true,
      cellClassName: FIT_CONTENT,
      render: (_value, patient) => (
        <div className='flex items-center gap-1.5 whitespace-nowrap'>
          <span className='font-medium'>{patient.last_name || EMPTY}</span>
          {patient.is_test_patient && (
            <TestPatientBadge className='whitespace-nowrap' />
          )}
          {patient.import_source && !patient.import_badge_hidden_at && (
            <ImportedPatientBadge
              importedAt={patient.imported_at}
              className='whitespace-nowrap'
            />
          )}
          {showSyncReview && (patient.sync_review_new_count ?? 0) > 0 && (
            <Badge
              variant='outline'
              className='whitespace-nowrap font-medium'
              data-testid='patient-row-sync-review'
            >
              {patientCardSyncLine(patient.sync_review_new_count ?? 0)}
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'first_name',
      header: 'First name',
      accessor: 'first_name',
      enableSorting: true,
      cellClassName: FIT_CONTENT,
    },
    {
      id: 'mrn',
      header: 'MRN',
      accessor: 'mrn',
      enableSorting: true,
      cellClassName: FIT_CONTENT,
    },
    {
      id: 'episode_start',
      header: 'Episode dates',
      accessor: episodeDatesLabel,
      enableSorting: true,
      cellClassName: `${FIT_CONTENT} tabular-nums`,
    },
    {
      id: 'date_of_birth',
      header: 'Date of birth',
      accessor: patient => formatDateToMMDDYYYY(patient.date_of_birth) || EMPTY,
      enableSorting: true,
      cellClassName: `${FIT_CONTENT} tabular-nums`,
    },
    {
      id: 'insurance_name',
      header: 'Primary insurance',
      accessor: patient => patient.insurance_name || EMPTY,
      enableSorting: true,
      cellClassName: FLEXIBLE,
      render: (_value, patient) => (
        <OverflowTooltipText text={patient.insurance_name || EMPTY} />
      ),
    },
    {
      id: 'city',
      header: 'City',
      accessor: patient => patient.city || EMPTY,
      enableSorting: true,
      // Hugs its content up to the cap, so it never takes spare width.
      cellClassName: FIT_CONTENT,
      render: (_value, patient) => (
        <OverflowTooltipText
          text={patient.city || EMPTY}
          className='max-w-[10rem]'
        />
      ),
    },
    {
      id: 'primary_dx_code',
      header: 'Primary diagnosis',
      accessor: patient => patient.primary_dx_code ?? '',
      enableSorting: true,
      cellClassName: FLEXIBLE,
      render: (_value, patient) => {
        if (!patient.primary_dx_code) return EMPTY;
        const label =
          `${patient.primary_dx_code} ${patient.primary_dx_description ?? ''}`.trim();
        return <OverflowTooltipText text={label} />;
      },
    },
    {
      id: 'notes',
      header: 'Notes',
      accessor: patient => patient.episode_notes_preview.length,
      enableSorting: false,
      cellClassName: 'w-px',
      // Hover shows the newest note; a click lists every note, read-only.
      // The box keeps the icon's height when there is no icon, so every row
      // is the same height.
      render: (_value, patient) => (
        <div className='flex h-8 items-center' data-testid='patient-notes-cell'>
          <EpisodeNotesPill
            variant='icon'
            side='bottom'
            notes={patient.episode_notes_preview}
            ariaLabel={`Episode notes (${patient.episode_notes_preview.length})`}
            data-testid='patient-episode-notes'
          />
        </div>
      ),
    },
  ];
}

export interface PatientManagerTableProps {
  patients: Patient[];
  isLoading: boolean;
  sorting: ReportTableSort | null;
  onSortingChange: (next: ReportTableSort | null) => void;
  onOpen: (patient: Patient) => void;
  /** Take Over v2's sync review is readable: show its count beside the name. */
  showSyncReview: boolean;
}

/** The Roger charts view of the Patient Manager: one server page, a row per chart. */
export const PatientManagerTable: React.FC<PatientManagerTableProps> = ({
  patients,
  isLoading,
  sorting,
  onSortingChange,
  onOpen,
  showSyncReview,
}) => {
  const columns = useMemo(() => buildColumns(showSyncReview), [showSyncReview]);
  // Rows from the last finished load stay up (dimmed) while the next one runs,
  // so fit-to-content columns don't collapse under empty skeleton rows. Only a
  // load that started after mount counts: until then the shared store list
  // may hold another view's rows.
  const [wasLoading, setWasLoading] = useState(isLoading);
  const [loadStartedHere, setLoadStartedHere] = useState(false);
  const [loadedPatients, setLoadedPatients] = useState<Patient[] | null>(null);
  if (isLoading !== wasLoading) {
    setWasLoading(isLoading);
    if (isLoading) setLoadStartedHere(true);
  }
  if (!isLoading && loadStartedHere && loadedPatients !== patients) {
    setLoadedPatients(patients);
  }
  const staleRows = isLoading ? loadedPatients : null;
  return (
    <ReportTable
      data={staleRows ?? patients}
      columns={columns}
      getRowId={patient => patient.id}
      caption='Patients'
      sorting={sorting}
      onSortingChange={onSortingChange}
      isLoading={isLoading && !staleRows}
      onRowClick={onOpen}
      rowAriaLabel={patient =>
        `Open ${[patient.first_name, patient.last_name].filter(Boolean).join(' ')}`
      }
      pinFirstColumn
      appearance='soft'
      // Lets the table shrink to its container (overriding ReportTable's
      // min-w-max); one-line headers keep identifying columns from wrapping.
      className={`min-w-0 [&_th]:whitespace-nowrap${staleRows ? ' opacity-60' : ''}`}
      maxHeight='none'
      emptyState={
        <EmptyState
          title='No patients match'
          description='Change the search or filters.'
        />
      }
    />
  );
};

export default PatientManagerTable;
